import React from 'react';
import { Box } from '@mui/material';
import {
    gridClasses,
    GridColDef,
    GridRowSelectionModel,
    GridValidRowModel,
    GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    GridSingleSelectColDef,
    GridActionsCellItem,
} from '@mui/x-data-grid-pro';
import i18n, { defaultLanguage } from '../../../localization/i18n';
import { TFunction } from 'react-i18next';
import { IViewStructureNode } from '../../../redux/project/sliceProjectViewData';
import {
    IDocDefaultFields,
    IFieldConfig,
} from '../../../redux/project/documents/sliceProjectDocuments';
import { parseUsername } from '../../../util/DataUtils';
import DocTableCategoryRow from './DocTableCategoryRow';
import DocTableCell from './DocTableCell';
import DocTableDetailToggleButton from './DocTableDetailToggleButton';
import DocTableMenu from './DocTableMenu';
import {
    ISelectedDocuments,
    updateProjectDocumentSummary,
    updateSelectedDocuments,
} from '../../../redux/project/documents/sliceProjectSelectedDocuments';
import { customFilterOperators } from './CustomFilters/CustomFilter';
import { dateFilterOperators } from './CustomFilters/DateFilter';
import { getDocTypeOrDefault } from '../../../util/DocUtils';
import { IProjectDocType } from '../../../redux/project/sliceProjectDocTypes';
import { mdiDotsVertical, mdiClose } from '@mdi/js';
import { actionColumnWidth } from '../../../Theme';
import TranslatedCategoryName from '../TranslatedCategoryName/TranslatedCategoryName';
import Icon from '@mdi/react';
import { deDE, enUS } from '@mui/x-data-grid/locales';
import store from '../../../redux/store';
import { isSubpathOf } from '../../../util/StructureUtils';

/**
 * Processes an array of document objects from Notes so that they can be rendered in the DocTable.
 * @param documents - Array of documents to be processed.
 * @param fields - Array of field configurations.
 */
export const processDocuments = (documents: IDocDefaultFields[], fields: IFieldConfig[]) =>
    documents.map(obj => {
        const out = {
            NoteId: obj.NoteId,
            IdDocIndex: obj.IdDocIndex,
            UniversalID: obj.UniversalID,
            ChildCount: obj.ChildCount,
            RedliningCount: obj.RedliningCount,
            Files: obj.Files,
            TestMode: obj.TestMode,
            WfId: obj.wfId,
            DocType: obj.DocType,
            ewfSignStatus: obj.ewfSignStatus,
            JobBackend: obj.JobBackend,
            DocIndexNext: obj.DocIndexNext,
        };

        fields.forEach(x => (out[x.field] = parseFieldValue(x, obj)));

        if (obj['Validation']) {
            return { ...out, Validation: obj['Validation'] };
        }

        return out;
    });

/**
 * Creates the column definitions for the DataGrid.
 * @param fieldSchema - Array of fields that the user wants to show in the grid.
 * @param fields - Field configurations from Storage.
 * @param rows - Array of rows for the DataGrid.
 * @param disableClick - (OPTIONAL) Flag that prevents on-click events for cells being triggered.
 * @returns Column definitions.
 */
export const getColumns = (
    fieldSchema: string[],
    fields: IFieldConfig[],
    rows: GridValidRowModel[],
    transalteFn: TFunction,
    disableClick?: boolean,
    highlightText?: string,
): GridColDef[] =>
    fieldSchema.map(x => {
        const fieldConfig = fields?.filter(f => f.field === x)[0];

        if (!fieldConfig) {
            return null;
        }

        const {
            field,
            headerName,
            sortable,
            filterable,
            resizable,
            editable,
            align,
            width,
            disableColumnMenu,
            disableReorder,
            hideable,
            hoverBtns,
        } = fieldConfig;

        const { fields: notesFields } = fieldConfig;
        let { type } = fieldConfig;

        const config: GridColDef = {
            field,
            headerName: headerName[i18n.language] ?? headerName[defaultLanguage],
            resizable,
            hideable,
            filterable,
            sortable,
            editable,
            disableColumnMenu,
            disableReorder,
            align: align ?? 'left',
        };

        if (width) {
            config.width = width;
        }

        if (editable && notesFields.length === 1 && notesFields[0] !== 'DocNumber') {
            type = 'editableString';
        }

        switch (type) {
            case 'icon':
            case 'wfHistory':
                config.type = 'singleSelect';
                (config as GridSingleSelectColDef).valueOptions = getUniqueValues(
                    rows,
                    fieldConfig.field,
                );
                config.filterOperators = customFilterOperators;
                break;

            case 'user':
                config.type = 'singleSelect';
                (config as GridSingleSelectColDef).valueOptions = getUniqueValues(
                    rows,
                    fieldConfig.field,
                );
                break;

            case 'date':
            case 'dateRelative':
                config.valueGetter = value => {
                    config.type = value ? 'date' : 'string';
                    return value;
                };

                config.filterOperators = dateFilterOperators(transalteFn);
                break;

            case 'number':
            case 'numberRelative':
                config.type = 'number';
                break;

            case 'files':
                config.disableExport = true;
        }

        config.renderCell = ({ value, row }) => {
            return (
                <DocTableCell
                    {...{
                        type,
                        value,
                        row,
                        hoverBtns,
                        disableClick,
                        notesField: notesFields[0],
                        isEditable: editable,
                        highlightText,
                    }}
                />
            );
        };

        return config;
    });

/**
 * Process categories from viewStructure to row models.
 * @param viewStructure - Data structure from the backend.
 * @param category - Name of the category.
 * @returns GridRowModels for categories.
 */
export const getCategoryRows = (
    viewStructure: IViewStructureNode[],
    category: string,
): GridValidRowModel[] => {
    if (!viewStructure) {
        return [];
    }

    const match = category && viewStructure?.filter(x => x.id === category)[0]?.category;
    const level = match?.split('\\').length ?? 0;

    return viewStructure
        .filter(x =>
            match
                ? /** Remove categories that are not direct children of the matched category. */
                  isSubpathOf(x.category, match) && x.category.split('\\').length === level + 1
                : /** Remove "empty" category names, and non top-level categories. */
                  !!x?.category && x.category.indexOf('\\') < 0,
        )
        .map(x => ({
            NoteId: x.id,
            CategoryName: x.category.replace(`${match}\\`, ''),
        }));
};

/**
 * Creates the new list of selected document ids for the DataGrid.
 * @param rows -> DataTable rows.
 * @param selectedDocuments -> Existing selected document ids.
 * @param rowSelectionModel -> Array of selected row ids from the DataGrid.
 * @returns Updated array of selected document ids.
 */

export const getSelectedDocIds = (
    rows: GridValidRowModel[],
    selectedDocuments: ISelectedDocuments,
    rowSelectionModel: GridRowSelectionModel,
) => {
    const selectedDocIds: string[] = rows
        .filter(x => rowSelectionModel.indexOf(x.NoteId) >= 0)
        .map(x => x.UniversalID);

    const toRemove = rows.map(x => x.UniversalID).filter(x => selectedDocIds.indexOf(x) < 0);

    const documentIds = [...(selectedDocuments?.documentIds ?? [])].filter(
        x => toRemove.indexOf(x) < 0,
    );

    selectedDocIds
        .filter(x => selectedDocuments?.documentIds?.indexOf(x) < 0)
        .forEach(x => documentIds.indexOf(x) < 0 && documentIds.push(x));

    return documentIds;
};

/**
 * Creates the definition for the "actions" column that houses the master detail and menu controls for a row.
 * @param fieldSchema -> Array of the other columns to be shown in the DataGrid.
 */
export const createActionColDef = (
    fieldSchema: string[],
    docTypes: IProjectDocType[],
    transalteFn: TFunction,
): GridColDef => ({
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    headerName: 'Action',
    headerAlign: 'center',
    disableColumnMenu: true,
    disableReorder: true,
    filterable: false,
    resizable: false,
    sortable: false,
    hideable: false,
    align: 'center',
    width: actionColumnWidth,
    renderCell: ({ row, value }) => {
        if (row.CategoryName) {
            return (
                <DocTableCategoryRow
                    id={row.NoteId}
                    name={<TranslatedCategoryName categoryName={row.CategoryName as string} />}
                />
            );
        }

        return (
            <>
                <DocTableDetailToggleButton show={Boolean(row.ChildCount)} expanded={value} />
                <DocTableMenu
                    docType={getDocTypeOrDefault(row, docTypes)}
                    universalId={row.UniversalID}
                    currentDocument={row}
                    icon={mdiDotsVertical}
                />
            </>
        );
    },
    renderHeader: () => {
        return <Box className={gridClasses.columnHeaderTitle}>{transalteFn('action')}</Box>;
    },

    colSpan: (_, row) => {
        return row.CategoryName ? fieldSchema.length + 1 : 1;
    },
});

export const createActionsColumn = (): GridColDef => ({
    field: 'actions',
    type: 'actions',
    headerName: 'Action',
    hideable: false,
    disableColumnMenu: true,
    disableReorder: true,
    filterable: false,
    resizable: false,
    sortable: false,
    align: 'center',
    getActions: ({ row }) => {
        return [
            <GridActionsCellItem
                key='delete'
                icon={<Icon path={mdiClose} size={1} />}
                label='Delete'
                onClick={() => deleteSelectedDocuments(row.UniversalID)}
            />,
        ];
    },
});

export const deleteSelectedDocuments = (rowId: string) => {
    const { projectSelectedDocuments } = store.getState();
    const {
        storage: { areaName, replicaId, documentIds },
        data: projectDocumentSummary,
    } = projectSelectedDocuments;
    const newDocumentIds = documentIds?.filter(documentId => documentId !== rowId);
    const newProjectDocumentSummary = projectDocumentSummary?.documents?.filter(
        document => document.UniversalID !== rowId,
    );
    store.dispatch(
        updateSelectedDocuments({
            replicaId,
            areaName,
            documentIds: newDocumentIds,
        }),
    );
    store.dispatch(
        updateProjectDocumentSummary({
            ...projectDocumentSummary,
            documents: newProjectDocumentSummary,
        }),
    );
};

/**
 * Gets a list of the unique property values from an array of objects.
 * @param data - Array of objects.
 * @param name - Property name to collate.
 */
export const getUniqueValues = <T,>(data: T[], name: string) => [
    ...new Set(data.map(x => x[name]).filter(Boolean)),
];

/**
 * Finds and replaces template tags ("{{value}}"") in a given string.
 * @param str - Target string.
 * @param obj - Object to use as a reference for replacement values.
 * @returns Updated template string, or an empty string if no replacement values were found.
 */
export const parseValueTemplate = <T,>(str: string, obj: T): string => {
    let out = `${str}`;
    const matchOut = out.match(/\{\{.+?\}\}/g);

    if (!matchOut) return out;

    const replacements = matchOut
        .map(match => {
            let prop = obj[match.replace(/[{}]/g, '')];

            if (prop && Array.isArray(prop)) {
                prop = prop.join('&&');
            }

            out = out.replace(match, prop ?? '');

            return prop;
        })
        .filter(Boolean);

    return replacements.length > 0 ? out : '';
};

/**
 * Parses the value of a given field.
 * @param field - Target field.
 * @param obj - Object to use as a reference.
 */
export const parseFieldValue = <T,>(field: IFieldConfig, obj: T) => {
    if (field.value) {
        return parseValueTemplate(field.value, obj);
    }

    const firstFieldValue = obj[field.fields[0]];

    if (field.type === 'user') {
        return parseUsername(firstFieldValue);
    }

    return firstFieldValue;
};

/**
 * Returns the localisation strings for the DataGrid for the current application langauge.
 */
export const getGridTranslations = () => {
    switch (i18n.language) {
        case 'de':
            return {
                ...deDE.components.MuiDataGrid.defaultProps.localeText,
                filterOperatorDoesNotContain: 'enthält nicht',
                filterOperatorDoesNotEqual: 'ungleich',
                filterOperatorIs: 'ist gleich',
            };

        case 'en':
        default:
            return {
                ...enUS.components.MuiDataGrid.defaultProps.localeText,
                filterOperatorIs: 'equals',
            };
    }
};
